import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../../contexts/AuthContext';

interface DashboardHeaderProps {
  stats: {
    totalReplies: number;
    weeklyReplies: number;
    empathyReceived: number;
    responseRate: number;
  };
  onShiftClick: () => void;
}

export default function DashboardHeader({ stats, onShiftClick }: DashboardHeaderProps) {
  const { user, signOut } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="bg-white shadow-sm sticky top-0 z-40">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 sm:gap-3">
            <img 
              src="https://public.readdy.ai/ai/img_res/6ba73c54ac91342aea6b8afb48991f4a.jpg" 
              alt="スマイル・ポート" 
              className="h-8 w-8 sm:h-10 sm:w-10 object-contain"
            />
            <span className="text-lg sm:text-2xl font-bold text-teal-600">スマイル・ポート</span>
          </Link>

          {/* デスクトップメニュー */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/board" className="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer">
              掲示板
            </Link>
            <Link to="/manual" className="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer">
              マニュアル
            </Link>
            <Link to="/partner/list" className="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer">
              パートナー
            </Link>
            <Link to="/partner/dashboard" className="text-teal-600 font-medium cursor-pointer">
              ダッシュボード
            </Link>
            <button
              onClick={signOut}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              ログアウト
            </button>
          </div>

          {/* モバイルハンバーガーメニュー */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl text-gray-600`}></i>
          </button>
        </div>

        {/* モバイルメニュー */}
        {menuOpen && (
          <div className="md:hidden py-4 border-t border-gray-100">
            <div className="flex flex-col space-y-3">
              <Link to="/board" className="px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer">
                掲示板
              </Link>
              <Link to="/manual" className="px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer">
                マニュアル
              </Link>
              <Link to="/partner/list" className="px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer">
                パートナー
              </Link>
              <Link to="/partner/dashboard" className="px-4 py-2 text-teal-600 font-medium bg-teal-50 rounded-lg cursor-pointer">
                ダッシュボード
              </Link>
              <button
                onClick={signOut}
                className="mx-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap text-left"
              >
                ログアウト
              </button>
            </div>
          </div>
        )}
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800 mb-1">
              パートナーダッシュボード
            </h1>
            <p className="text-sm sm:text-base text-gray-600">
              ようこそ、{user?.user_metadata?.nickname || 'パートナー'}さん
            </p>
          </div>
          <button
            onClick={onShiftClick}
            className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-xl hover:shadow-lg transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 text-sm sm:text-base"
          >
            <i className="ri-time-line"></i>
            見守り時間設定
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl sm:rounded-2xl p-4 sm:p-6">
            <div className="flex items-center justify-between mb-2">
              <i className="ri-chat-check-line text-xl sm:text-2xl text-teal-600"></i>
            </div>
            <p className="text-xs sm:text-sm text-teal-700 mb-1">総回答数</p>
            <p className="text-2xl sm:text-3xl font-bold text-teal-600">{stats.totalReplies}</p>
          </div>

          <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl sm:rounded-2xl p-4 sm:p-6">
            <div className="flex items-center justify-between mb-2">
              <i className="ri-calendar-check-line text-xl sm:text-2xl text-amber-600"></i>
            </div>
            <p className="text-xs sm:text-sm text-amber-700 mb-1">今週の回答</p>
            <p className="text-2xl sm:text-3xl font-bold text-amber-600">{stats.weeklyReplies}</p>
          </div>

          <div className="bg-gradient-to-br from-rose-50 to-rose-100 rounded-xl sm:rounded-2xl p-4 sm:p-6">
            <div className="flex items-center justify-between mb-2">
              <i className="ri-heart-3-line text-xl sm:text-2xl text-rose-600"></i>
            </div>
            <p className="text-xs sm:text-sm text-rose-700 mb-1">共感された数</p>
            <p className="text-2xl sm:text-3xl font-bold text-rose-600">{stats.empathyReceived}</p>
          </div>

          <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl sm:rounded-2xl p-4 sm:p-6">
            <div className="flex items-center justify-between mb-2">
              <i className="ri-percent-line text-xl sm:text-2xl text-emerald-600"></i>
            </div>
            <p className="text-xs sm:text-sm text-emerald-700 mb-1">回答率</p>
            <p className="text-2xl sm:text-3xl font-bold text-emerald-600">{stats.responseRate}%</p>
          </div>
        </div>
      </div>
    </div>
  );
}
